import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Stack,
  Button,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";

interface ProgressStep {
  label: string;
  description?: string;
}

interface ResourceCreationModalProps {
  open: boolean;
  currentStep: number;
  steps: ProgressStep[];
  error?: string | null;
  failedStep?: number | null;
  canContinueToPortal?: boolean;
  warnings?: string[];
  onContinue?: () => void;
}

const ResourceCreationModal: React.FC<ResourceCreationModalProps> = ({
  open,
  currentStep,
  steps,
  error,
  failedStep,
  canContinueToPortal = false,
  warnings = [],
  onContinue,
}) => {
  const progress = error
    ? ((failedStep ?? 0) / steps.length) * 100
    : ((currentStep + 1) / steps.length) * 100;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <DialogContent>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            {error
              ? canContinueToPortal
                ? "Setup Partially Complete"
                : "Setup Failed"
              : "Setting Up Your Account"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error
              ? canContinueToPortal
                ? "Your account is ready, but some features need attention"
                : `Error occurred during ${
                    typeof failedStep === "number" &&
                    failedStep >= 0 &&
                    failedStep < steps.length
                      ? steps[failedStep].label
                      : "setup"
                  }`
              : "Please wait while we create your profile..."}
          </Typography>
        </Box>

        {/* Show warnings if any */}
        {warnings.length > 0 && (
          <Box
            sx={{
              p: 2,
              bgcolor: "warning.lighter",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "warning.main",
              mb: 3,
            }}
          >
            <Typography
              variant="body2"
              color="warning.dark"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Some features couldn't be set up:
            </Typography>
            {warnings.map((warning, idx) => (
              <Typography
                key={idx}
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                • {warning}
              </Typography>
            ))}
          </Box>
        )}

        {error ? (
          <>
            <Box
              sx={{
                p: 3,
                bgcolor: canContinueToPortal
                  ? "warning.lighter"
                  : "error.lighter",
                borderRadius: 2,
                border: "1px solid",
                borderColor: canContinueToPortal
                  ? "warning.main"
                  : "error.main",
                mb: 3,
              }}
            >
              <Typography
                variant="body2"
                color={canContinueToPortal ? "warning.dark" : "error.main"}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                {error}
              </Typography>
              {typeof failedStep === "number" &&
                failedStep >= 0 &&
                failedStep < steps.length &&
                steps[failedStep]?.description && (
                  <Typography variant="caption" color="text.secondary">
                    Failed at: {steps[failedStep].description}
                  </Typography>
                )}
            </Box>

            {/* Show progress with error state */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              {steps.map((step, index) => {
                const isComplete = index < (failedStep ?? 0);
                const isFailed = index === failedStep;
                const isPending = index > (failedStep ?? 0);

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isFailed
                        ? canContinueToPortal
                          ? "warning.lighter"
                          : "error.lighter"
                        : "transparent",
                      border: isFailed ? "1px solid" : "none",
                      borderColor: isFailed
                        ? canContinueToPortal
                          ? "warning.main"
                          : "error.main"
                        : "transparent",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isComplete && (
                        <CheckCircleIcon
                          sx={{ color: "success.main", fontSize: 24 }}
                        />
                      )}
                      {isFailed && (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: canContinueToPortal
                              ? "warning.main"
                              : "error.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          {canContinueToPortal ? "⚠" : "✕"}
                        </Box>
                      )}
                      {isPending && (
                        <RadioButtonUncheckedIcon
                          sx={{ color: "grey.300", fontSize: 24 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: isFailed ? 600 : 400,
                          color: isComplete
                            ? "success.main"
                            : isFailed
                            ? canContinueToPortal
                              ? "warning.dark"
                              : "error.main"
                            : "text.secondary",
                        }}
                      >
                        {step.label}
                      </Typography>
                      {step.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {step.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {/* Action buttons */}
            <Box sx={{ mt: 3, textAlign: "center" }}>
              {canContinueToPortal ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onContinue}
                    sx={{ mr: 1 }}
                  >
                    Continue to Portal
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 2 }}
                  >
                    You can complete the setup later from your dashboard
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Please try again or contact support if the issue persists.
                  </Typography>
                </>
              )}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    bgcolor: "success.main",
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Step {currentStep + 1} of {steps.length}
              </Typography>
            </Box>

            <Stack spacing={2}>
              {steps.map((step, index) => {
                const isComplete = index < currentStep;
                const isCurrent = index === currentStep;
                const isPending = index > currentStep;

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isCurrent ? "action.selected" : "transparent",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isComplete && (
                        <CheckCircleIcon
                          sx={{ color: "success.main", fontSize: 24 }}
                        />
                      )}
                      {isCurrent && (
                        <CircularProgress
                          size={24}
                          sx={{ color: "primary.main" }}
                        />
                      )}
                      {isPending && (
                        <RadioButtonUncheckedIcon
                          sx={{ color: "grey.300", fontSize: 24 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: isCurrent ? 600 : 400,
                          color: isComplete
                            ? "success.main"
                            : isCurrent
                            ? "text.primary"
                            : "text.secondary",
                        }}
                      >
                        {step.label}
                      </Typography>
                      {step.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {step.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResourceCreationModal;
